import { PageType, ShareType } from '@utils/pageConfig/Types';
import { SaleType } from '@models/Products';
import { ExportExploreTrendsStack, KpiData, KpiObject } from '@data-visualization/trends-stack-chart/Types';
import { generateUniqueChartKeys } from '@data-visualization/utilities/GenerateUniqueChartKeys';
import { ChartKeys } from '@const/ChartKeys';
import { Chart } from 'highcharts';
import { SortDirection, SortFieldDisplayProps } from '@components/design/migrated/Table/Sorting/types';
import { SortKeys } from '@models/PerformanceAnalysisboard';
import { GrowthTypes } from '@components/comparison-growth-selector/Types';
import { KpiTypeTrendStackChart } from '@pages/explore/ExploreContants';
import { ColumnHeaderForCL } from '@models/CategoryLeaderboard';
import { upperCase } from 'lodash';

export interface LegendEvent {
  target: { index: number };
  type?: string;
}

export interface MappedChartSeries {
  name: string;
  data1: number[];
  data2: number[];
  color: string;
}

export type Entity = 'market' | 'retailer';
export type Measure = 'Unit' | 'Revenue';

export const extractValue = (s: string): number => {
  const nums = (s.match(/\d+(\.\d+)?/g) || []).map(Number);
  if (nums.length >= 2) {
    const a = nums[nums.length - 2];
    const b = nums[nums.length - 1];
    return (a + b) / 2;
  }
  if (nums.length === 1) {
    const n = nums[0];
    if (/^<+/.test(s)) return n / 2;
    if (/^>+/.test(s)) return n + Infinity;
    return n;
  }
  return NaN;
};

export const compareSeries = (a: MappedChartSeries, b: MappedChartSeries, dir: number = 1): number => {
  const va = extractValue(a.name);
  const vb = extractValue(b.name);
  const aNum = !isNaN(va);
  const bNum = !isNaN(vb);
  if (aNum && bNum) return (va - vb) * dir;
  if (aNum && !bNum) return -1 * dir;
  if (!aNum && bNum) return dir;
  return a.name.localeCompare(b.name) * dir;
};

export const mapAndSortChartData = (
  selectedChartData: KpiData[],
  stackedChartSortingOrder: SortFieldDisplayProps | undefined,
  getBrandColorMap: (names: string[]) => Record<string, string>,
): MappedChartSeries[] => {
  const brandColors = getBrandColors(selectedChartData, getBrandColorMap);
  const mappedData: MappedChartSeries[] = selectedChartData.map((series) => ({
    name: series.name,
    data1: series.data.slice(0, 2),
    data2: series.data.slice(2),
    color: brandColors[upperCase(series.name)],
  }));

  if (!stackedChartSortingOrder) return mappedData;

  const { sortKey, sortDirection } = stackedChartSortingOrder;
  const dir = sortDirection === SortDirection.asc ? 1 : -1;

  const groupItems = mappedData.filter((s) => s.name !== 'OTHERS');
  const others = mappedData.filter((s) => s.name === 'OTHERS');

  if (sortKey === SortKeys.Alphabetically) {
    groupItems.sort((a, b) => compareSeries(a, b, dir));
    return [...groupItems, ...others];
  }

  if (sortDirection === SortDirection.asc) {
    return [...groupItems.slice().reverse(), ...others];
  }
  return [...groupItems, ...others];
};

type RevenueValues = {
  revenueChange: number | null | '';
  totalRevenue: number | null;
  revenueChangeAbsolute?: number | null;
  segmentShare: number | null;
};

export const getKpiData = (
  kpi: KpiObject[],
  shareType: ShareType,
  saleType: ColumnHeaderForCL,
  isMarketFlag: boolean,
  isSegment: boolean,
): KpiData[] | KpiObject[] => {
  const kpiType = `${isMarketFlag ? 'market' : 'retailer'}${
    [ColumnHeaderForCL.marketVolume, ColumnHeaderForCL.yourVolume].includes(saleType) ? 'Unit' : 'Revenue'
  }${shareType === ShareType.Absolute ? 'Absolute' : 'Share'}`;
  const kpiObject = kpi?.find((item) => item.kpiType === kpiType);
  if (isSegment) {
    return kpi[0]?.kpiData;
  }
  return kpiObject ? kpiObject.kpiData : [];
};

export const formatKpiValues = (num: number, shareType: ShareType) => {
  if (shareType === ShareType.Absolute) {
    if (num >= 1_000_000_000) {
      return `${(num / 1_000_000_000).toFixed(1).replace(/\.00$/, '')}B`;
    } else if (num >= 1_000_000) {
      return `${(num / 1_000_000).toFixed(1).replace(/\.00$/, '')}M`;
    }
    return num.toLocaleString();
  } else {
    return `${Number.isInteger(num) ? num : num.toFixed(1)}%`;
  }
};

export const getBrightness = (hex) => {
  if (typeof hex !== 'string' || !hex.startsWith('#') || hex.length !== 7) return 255;
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return (r * 299 + g * 587 + b * 114) / 1000;
};

export const formatRevenue = (num, isSegment) => {
  if (isSegment) return `${num.value}${num.unit}`;
  if (!Number.isFinite(num)) {
    throw new Error('Invalid number');
  }
  return Number.isInteger(num) ? `${num}M` : `${num.toFixed(1)}M`;
};

export const getBrandColors = (
  selectedChartData: KpiData[],
  getBrandColorMap: (names: string[]) => Record<string, string>,
) => {
  const brandNames = selectedChartData.map((item) => item.name);
  return { ...getBrandColorMap(brandNames), OTHERS: '#d6d9dc' };
};

export const exportChartKey = (
  data: ExportExploreTrendsStack,
  selectedAbsoluteType: ShareType,
  selectedLineChartSaleType: SaleType,
  isMarket: boolean,
  isSegment: boolean = false,
) => {
  const transformChartData = (chartData) => {
    return chartData.map((item) => ({
      groupName: item.name,
      data: item.data.map((value, periodIndex) => ({
        period: periodIndex,
        value,
      })),
    }));
  };

  const kpiChartData = getKpiData(data.series, selectedAbsoluteType, selectedLineChartSaleType, isMarket, isSegment);
  const transformedData = transformChartData(kpiChartData);
  if (isSegment) {
    return generateUniqueChartKeys(transformedData, ChartKeys[PageType.EXPLORE].SEGMENT_STACK);
  }
  return isMarket
    ? generateUniqueChartKeys(transformedData, ChartKeys[PageType.EXPLORE].STACK_MARKET)
    : generateUniqueChartKeys(transformedData, ChartKeys[PageType.EXPLORE].STACK_RETAILER);
};

export const getRevenueValues = (
  index: number,
  data: ExportExploreTrendsStack,
  isMarket: boolean,
  selectedGrowthType: GrowthTypes,
  selectedSaleType: ColumnHeaderForCL,
  isSegment: boolean,
): RevenueValues => {
  const entity: Entity = isMarket ? 'market' : 'retailer';
  const measure: Measure = [ColumnHeaderForCL.marketVolume, ColumnHeaderForCL.yourVolume].includes(selectedSaleType)
    ? 'Unit'
    : 'Revenue';

  if (isSegment) {
    return {
      totalRevenue: data.totalRevenueInMillion[index],
      revenueChange: data.revenueChangeInPercent[index],
      revenueChangeAbsolute: data.revenueChangeInAbsolute[index],
      segmentShare: data.segmentShare[index],
    };
  }

  return {
    totalRevenue: getRevenueTotal(data, entity, measure, index),
    revenueChange: getChangeTotal(data, entity, measure, selectedGrowthType, index),
    segmentShare: null,
  };
};

export const getRevenueTotal = (
  data: ExportExploreTrendsStack,
  entity: Entity,
  measure: Measure,
  index: number,
): number => {
  const key = `${entity}Total${measure}InMillion` as keyof ExportExploreTrendsStack;
  return data[key][index];
};

export const getChangeTotal = (
  data: ExportExploreTrendsStack,
  entity: Entity,
  measure: Measure,
  growth: GrowthTypes,
  index: number,
): number | string => {
  const suffix = growth === GrowthTypes.actual ? 'ChangeAbsoluteInMillion' : 'ChangeInPercent';
  const key = `${entity}${measure}${suffix}` as keyof ExportExploreTrendsStack;
  const val = data[key][index];
  return growth === GrowthTypes.actual ? val : (val ?? '');
};

export const getFillColorAndDisplayText = (
  revenueChange: number | null | '',
  selectedGrowthType: GrowthTypes,
  revenueChangeAbsolute: number | null | '',
  isSegment: boolean,
): { fillColor: string; displayText: string } => {
  const defaultColor = 'rgba(100, 100, 100, 0)';
  const defaultText = selectedGrowthType === GrowthTypes.actual ? 'absolute' : '%change';

  if (isSegment && !revenueChangeAbsolute.value) {
    return { fillColor: defaultColor, displayText: defaultText };
  }

  if (revenueChange === '' || revenueChange === null) {
    return { fillColor: defaultColor, displayText: defaultText };
  }

  const fillColor = revenueChange < 0 ? '#bc1c0e' : '#167922';

  let displayText: string;
  if (selectedGrowthType === GrowthTypes.actual) {
    displayText = `${formatRevenue(isSegment ? revenueChangeAbsolute : revenueChange, isSegment)}`;
  } else {
    displayText = Number.isInteger(revenueChange) ? `${revenueChange}%` : `${revenueChange.toFixed(1)}%`;
  }

  return { fillColor, displayText };
};

type OverlayOpts = {
  className?: string;
  yOffset?: number;
  lineHeight?: number;
  paddingX?: number;
  css?: Highcharts.CSSObject;
};

export const renderTruncatingOverlay = (
  chart: Highcharts.Chart,
  lines: string[],
  {
    className = 'stack-column-revenue-label',
    yOffset = 40,
    lineHeight = 22,
    paddingX = 0,
    css = {
      fontSize: '14px',
      color: '#6f7582',
      fontWeight: '400',
      fontFamily: 'Lato, sans-serif',
    },
  }: OverlayOpts = {},
) => {
  chart.container.querySelectorAll(`.${className}`).forEach((n) => n.remove());
  const maxWidth = chart.plotWidth + 100;

  const measure = (txt: string) => {
    const truncatedText = chart.renderer.text(txt, -9999, -9999).css(css).add();
    const w = truncatedText?.getBBox?.().width ?? 0;
    truncatedText.destroy();
    return w;
  };

  const truncate = (txt: string) => {
    if (maxWidth === 0) return '…';
    if (measure(txt) <= maxWidth) return txt;
    let lo = 0,
      hi = txt.length,
      best = '…';
    while (lo <= hi) {
      const mid = (lo + hi) >> 1;
      const cand = `${txt.slice(0, Math.max(0, mid)).trim()}…`;
      if (measure(cand) <= maxWidth) {
        best = cand;
        lo = mid + 1;
      } else hi = mid - 1;
    }
    return best;
  };

  const textEl = chart.renderer
    .text('', chart.plotLeft + paddingX, chart.plotTop - yOffset)
    .css(css)
    .attr({ class: className })
    .add();

  lines.forEach((line, i) => {
    const tspan = document.createElementNS('http://www.w3.org/2000/svg', 'tspan');
    tspan.setAttribute('x', '0');
    tspan.setAttribute('dy', i === 0 ? '0' : String(lineHeight));
    tspan.textContent = truncate(line);
    (textEl as any).element.appendChild(tspan);
  });

  const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
  title.textContent = lines.join('\n');
  (textEl as any).element.appendChild(title);
  (textEl as any).css?.({ cursor: 'default' });

  return textEl;
};

type TextAlign = 'center' | 'left' | 'right';
type SegmentBlock = { name: string; start: number; end: number };

export const formatSegmentName = (value: string) =>
  value === 'GRAND_TOTAL' ? 'TOTAL' : value.replace(/_/g, ' ').trim();

export const countLeadingRunLength = (values: string[]) => {
  const first = values[0] ?? '';
  const idx = values.findIndex((v) => v !== first);
  return idx === -1 ? values.length : idx;
};

export const getEffectiveOffset = (dataKey: 'data1' | 'data2', customOffset: number, allSegments: string[]) =>
  dataKey === 'data2' ? Math.max(customOffset, countLeadingRunLength(allSegments)) : customOffset;

export const buildSegmentBlocks = (
  dataKey: 'data1' | 'data2',
  segments: string[],
  firstSegment: string,
  firstChartSpan: number | undefined,
  categoriesLength: number,
): SegmentBlock[] => {
  if (dataKey === 'data1') {
    const span = Math.max(1, Math.min(firstChartSpan ?? categoriesLength, categoriesLength));
    const name = formatSegmentName(segments.find(Boolean) ?? (firstSegment || 'Total'));
    return [{ name, start: 0, end: span - 1 }];
  }
  const blocks: SegmentBlock[] = [];
  let start = 0;
  for (let i = 1; i <= segments.length; i++) {
    if (i === segments.length || segments[i] !== segments[start]) {
      const name = formatSegmentName(segments[start] ?? '');
      if (name) blocks.push({ name, start, end: i - 1 });
      start = i;
    }
  }
  return blocks;
};

export const computeTextX = (
  align: TextAlign,
  leftX: number,
  rightX: number,
  width: number,
  padding: number,
): number => {
  let x: number;

  if (align === 'left') {
    x = leftX + padding;
  } else if (align === 'right') {
    x = rightX - padding;
  } else {
    x = leftX + width / 2;
  }

  return x;
};

export const createTextMeasurer =
  (chart: Highcharts.Chart, css: Highcharts.CSSObject) =>
  (t: string): number => {
    const node = chart.renderer.text(String(t), -9999, -9999).css(css).add();
    const w = node.getBBox().width ?? 0;
    node.destroy();
    return w;
  };

export const labelsEllipsis = (text: string, maxW: number, measure: (t: string) => number) => {
  if (measure(text) <= maxW) return text;
  let lo = 0,
    hi = text.length,
    best = '…';
  while (lo <= hi) {
    const mid = (lo + hi) >> 1;
    const cand = `${text.slice(0, mid).trim()}…`;
    if (measure(cand) <= maxW) ((best = cand), (lo = mid + 1));
    else hi = mid - 1;
  }
  return best;
};

export const wrapTwoLines = (text: string, maxW: number, measure: (t: string) => number) => {
  if (maxW <= 0) return ['', ''] as const;
  const words = text.split(/\s+/).filter(Boolean);
  if (!words.length) return ['', ''] as const;

  const take = (ws: string[]) => {
    let line = '',
      i = 0;
    for (; i < ws.length; i++) {
      const next = line ? `${line} ${ws[i]}` : ws[i];
      if (measure(next) <= maxW) line = next;
      else break;
    }
    if (!line && ws.length) return { line: labelsEllipsis(ws[0], maxW, measure), rest: ws.slice(1) };
    return { line, rest: ws.slice(i) };
  };

  const l1 = take(words);
  const l2 = take(l1.rest);
  const second = l2.rest.length ? labelsEllipsis(l2.line, maxW, measure) : l2.line;

  return [l1.line, second] as const;
};

export const renderSegmentHeaderRow = (
  chart: Highcharts.Chart,
  dataKey: 'data1' | 'data2',
  opts?: {
    yOffset?: number;
    xOffset?: number;
    firstChartSpan?: number;
    className?: string;
    align?: TextAlign;
    padding?: number;
  },
) => {
  const xAxis = chart.xAxis?.[0];
  if (!xAxis) return;

  const categories: string[] = (xAxis.categories as string[]) ?? [];
  if (!categories.length) return;

  const customOffset = Number(chart.customData?.labelOffset ?? 0);
  const allSegments: string[] = chart.customData?.data?.categorySegments ?? [];
  if (!allSegments.length) return;

  const effectiveOffset = getEffectiveOffset(dataKey, customOffset, allSegments);
  const segments = allSegments.slice(effectiveOffset, effectiveOffset + categories.length);
  if (!segments.length) return;

  const className = opts?.className ?? 'segment-header-row';
  chart.container?.querySelectorAll(`.${className}`)?.forEach((n) => n.remove());
  const group = chart.renderer.g().attr({ class: className }).add();

  const toX = (i: number) => xAxis.toPixels(i, true);
  const bandWidth = categories.length > 1 ? toX(1) - toX(0) : xAxis.len;
  const rowY = (xAxis as unknown as { bottom: number }).bottom - (opts?.yOffset ?? 24);

  const blocks = buildSegmentBlocks(
    dataKey,
    segments,
    String(allSegments[0] ?? ''),
    opts?.firstChartSpan,
    categories.length,
  );

  const align = opts?.align ?? 'center';
  const padding = opts?.padding ?? 6;
  const xShift = opts?.xOffset ?? 0;

  const css: Highcharts.CSSObject = {
    fontSize: '14px',
    fontWeight: '400',
    color: '#414752',
    fontFamily: 'Lato, sans-serif',
  };
  const measure = createTextMeasurer(chart, css);
  const lineHeight = 18;

  for (const { name, start, end } of blocks) {
    const leftX = toX(start) - bandWidth / 2;
    const rightX = toX(end) + bandWidth / 2;
    const width = Math.max(0, rightX - leftX);
    const maxW = Math.max(0, width - (align === 'center' ? 0 : 2 * padding));

    let textX = computeTextX(align, leftX, rightX, width, padding);
    textX += xShift;

    const [line1, line2] = wrapTwoLines(name, maxW, measure);

    if (line1) chart.renderer.text(line1, textX, rowY).attr({ align }).css(css).add(group);
    if (line2)
      chart.renderer
        .text(line2, textX, rowY + lineHeight)
        .attr({ align })
        .css(css)
        .add(group);
  }
};

export const formatLabel = (
  index: number,
  data: ExportExploreTrendsStack,
  isMarket: boolean,
  selectedGrowthType: GrowthTypes,
  selectedSaleType: KpiTypeTrendStackChart,
  isSegment: boolean,
) => {
  const { revenueChange, totalRevenue, segmentShare, revenueChangeAbsolute } = getRevenueValues(
    index,
    data,
    isMarket,
    selectedGrowthType,
    selectedSaleType,
    isSegment,
  );
  const value = totalRevenue !== null ? formatRevenue(totalRevenue, isSegment) : '';
  const { fillColor, displayText } = getFillColorAndDisplayText(
    revenueChange,
    selectedGrowthType,
    revenueChangeAbsolute,
    isSegment,
  );

  const segmentShareBlock =
    isSegment && segmentShare != null ? `<div style="margin-top: 7px">${segmentShare.toFixed(1)}%</div>` : '';

  return `<div style="text-align:center;line-height:1.2;font-family: Lato, sans-serif;" class="text-xs font-normal">
            <div style="color:${fillColor};margin-bottom: 10px">${displayText}</div>
            <div>${value}</div>
            ${segmentShareBlock}
          </div>`;
};

export const renderCustomLabels = (chart: Chart, dataKey: 'data1' | 'data2', isSegment = false) => {
  if (chart.customLabels) {
    chart.customLabels.forEach((label) => label.destroy());
  }
  chart.customLabels = [];

  const xAxis = chart.xAxis[0];
  const numCategories = xAxis.categories.length;

  const offsetY = isSegment ? 75 : 55;
  const yPos = chart.plotTop - offsetY;
  const chartOffset = dataKey === 'data1' ? 0.3 : 0.2;

  for (let i = 0; i < numCategories; i++) {
    const xPos = xAxis.toPixels(i + chartOffset, false);

    const labelText = chart.customData?.formatLabel(i);
    const label = chart.renderer
      .label(labelText, xPos, yPos, null, null, null, true)
      .css({
        color: '#6f7582',
        fontSize: '14px',
        fontFamily: 'Lato, sans-serif',
        textAlign: 'center',
      })
      .attr({ align: 'center' })
      .add();

    const bbox = label.getBBox();
    label.attr({ x: xPos - bbox.width / 2 });

    chart.customLabels.push(label);
  }
};

export function createAxisLabelFormatter() {
  return function tooltipFormatter() {
    const txt = this.value as string;
    const parts = txt.includes(' - ') ? txt.split(' - ') : [txt];
    if (parts.length === 2) {
      return ` <div style=" width: 80px; white-space: normal; text-align: center; word-wrap: break-word; "> ${parts[0].trim()}<br/>${parts[1].trim()} </div>`;
    }
    return ` <div style=" width: 80px; white-space: normal; text-align: center; word-wrap: break-word; "> ${txt} </div>`;
  };
}

export function createTooltipPositioner() {
  return function tooltipPositioner(labelWidth: number, labelHeight: number, point: any) {
    const chart = this.chart;
    const chartWidth = chart.chartWidth;
    let xPosition = point.plotX + 20;
    if (xPosition + labelWidth > chartWidth) {
      xPosition = chartWidth - labelWidth - 10;
    }
    const yPosition = (chart.chartHeight - labelHeight) / 2;
    return { x: xPosition, y: yPosition };
  };
}

export function createTooltipFormatter(selectedAbsoluteType: ShareType, isSegment) {
  return function tooltipFormatter() {
    const xValue = this.x;
    let html = `
      <div class="custom-tooltip bg-white shadow-lg p-2" style="min-width: 120px;font-family: Lato, sans-serif">
        <div class="text-heading font-bold border-b-1 border-default pb-1 mb-1">${xValue}</div>`;
    this.points.forEach((point: any, index: number) => {
      const borderClass = index !== this.points.length - 1 ? 'border-b-1 border-default' : '';
      html += `
        <div class="text-xs d-flex flex-row justify-between content-center items-center py-1 ${borderClass}">
          <span style="background: ${point.color};width: 10px;height:10px;border-radius: 2px"></span>
          <span class="ml-1 flex-1 truncate">${point.series.name}</span> 
          <span class="font-bold ml-3">${formatKpiValues(point.y, selectedAbsoluteType)}</span>
        </div>`;
    });
    html += `</div>`;
    return html;
  };
}

export function createDataLabelFormatter(selectedAbsoluteType: ShareType, isSegment) {
  return function dataLabelFormatter() {
    const { y, point } = this;
    const shapeHeight = point.shapeArgs?.height ?? 0;
    if (shapeHeight < 20) return null;
    const barColor = point.color ?? '#000000';
    const textColor = getBrightness(barColor) < 160 ? '#FFFFFF' : '#414752';
    return `<span style="color: ${textColor}; font-family: Lato, sans-serif; top: 50%; transform: translateY(-50%);" class="text-xs font-normal">
      ${formatKpiValues(y, selectedAbsoluteType)}</span>`;
  };
}

/**
 * Synchronizes the hover state of legend items across multiple charts.
 *
 * @param charts - Array of Highcharts Chart instances.
 * @param e - Event object containing the target index and event type.
 */
export function syncLegendHover(charts: Chart[], e: LegendEvent): void {
  const hoveredIndex = e.target.index;
  const stateType = e.type;
  charts.forEach((chart) => {
    if (!chart?.series) return;
    chart.series.forEach((series, index) => {
      if (stateType === 'hover') {
        series.points?.forEach((point) => point.setState(index === hoveredIndex ? 'hover' : 'inactive'));
      } else {
        series.points?.forEach((point) => point.setState(''));
      }
    });
  });
}

/**
 * Toggles the visibility of a series across multiple charts when a legend item is clicked.
 *
 * @param charts - Array of Highcharts Chart instances.
 * @param e - Event object containing the target index.
 */
export function syncLegendClick(charts: Chart[], e: LegendEvent): void {
  const seriesIndex = e.target.index;
  charts.forEach((chart) => {
    if (!chart?.series || seriesIndex === undefined || seriesIndex >= chart.series.length) return;
    const series = chart.series[seriesIndex];
    if (series) {
      series.setVisible(!series.visible, false);
      chart.redraw();
    }
  });
}
